import type { ReactNode } from "react";
import { buildUrl } from "./router";

export const CALL_ID_RE = /\b\d{15,19}\b/g;

// Models occasionally wrap call IDs in markdown code spans despite the
// prompts forbidding it (PET-645 UAT finding, prompts fixed in #189). The
// ID inside still linkifies — backticks aren't word chars, so \b holds —
// but the backticks would survive as stray literal characters around the
// link. Strip them when they wrap exactly a call ID; legitimate code
// spans are untouched.
const BACKTICKED_CALL_ID_RE = /`(\d{15,19})`/g;
export function stripCallIdBackticks(text: string): string {
  return text.replace(BACKTICKED_CALL_ID_RE, "$1");
}

export type CallRefInfo = {
  contact_phone: string | null;
  phone_ordinal: number | null;
  // Set on alias entries (a Dialpad duplicate-leg ID for the same
  // conversation): the viable call the alias resolves to. Links built
  // for an alias ID point at this canonical call's detail page.
  canonical_call_id?: string | null;
};

export type CallRefMap = Map<string, CallRefInfo>;

export type LinkifiedRef = {
  callId: string;
  phone: string;
  ordinal: number;
};

export function linkifyCallIds(
  rawText: string,
  keyPrefix: string,
  refMap?: CallRefMap,
  onRef?: (ref: LinkifiedRef) => void,
): ReactNode[] {
  const text = stripCallIdBackticks(rawText);
  const nodes: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  CALL_ID_RE.lastIndex = 0;
  while ((m = CALL_ID_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const id = m[0];
    const info = refMap?.get(id);
    // Alias refs (duplicate-leg IDs) link to the canonical call's page.
    const targetId = info?.canonical_call_id ?? id;
    const href = buildUrl("call-detail", null, null, { callId: targetId });
    let label: string = id;
    if (info && info.contact_phone && info.phone_ordinal != null) {
      label = `${info.contact_phone} (${info.phone_ordinal})`;
      onRef?.({
        callId: id,
        phone: info.contact_phone,
        ordinal: info.phone_ordinal,
      });
    }
    nodes.push(
      <a
        key={`${keyPrefix}-id${k++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80"
        data-call-id={targetId}
      >
        {label}
      </a>,
    );
    last = m.index + id.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Absolute URL to a call's detail page, used when building copy payloads
// so pasted links resolve outside the app shell.
export function buildCallHref(callId: string): string {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  return `${origin}${buildUrl("call-detail", null, null, { callId })}`;
}

// Plain text → escaped HTML, with raw Dialpad call IDs swapped for
// ``<phone> (<n>)`` anchor links pointing at absolute call-detail URLs.
// Everything outside the anchors is HTML-escaped, so the output is safe
// to embed in a clipboard ``text/html`` payload. (PET-642)
export function callIdsToHtml(rawText: string, refMap?: CallRefMap): string {
  const text = stripCallIdBackticks(rawText);
  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;
  CALL_ID_RE.lastIndex = 0;
  while ((m = CALL_ID_RE.exec(text)) !== null) {
    if (m.index > last) out += escapeHtml(text.slice(last, m.index));
    const id = m[0];
    const info = refMap?.get(id);
    // Alias refs (duplicate-leg IDs) link to the canonical call's page.
    const href = buildCallHref(info?.canonical_call_id ?? id);
    const label =
      info && info.contact_phone && info.phone_ordinal != null
        ? `${info.contact_phone} (${info.phone_ordinal})`
        : id;
    out += `<a href="${escapeHtml(href)}">${escapeHtml(label)}</a>`;
    last = m.index + id.length;
  }
  if (last < text.length) out += escapeHtml(text.slice(last));
  return out;
}

// Plain text → plain text, with call IDs rendered as ``<phone> (<n>) (url)``
// so the text/plain clipboard fallback still carries the links.
export function callIdsToPlainText(
  rawText: string,
  refMap?: CallRefMap,
): string {
  const text = stripCallIdBackticks(rawText);
  CALL_ID_RE.lastIndex = 0;
  return text.replace(CALL_ID_RE, (id) => {
    const info = refMap?.get(id);
    const href = buildCallHref(info?.canonical_call_id ?? id);
    const label =
      info && info.contact_phone && info.phone_ordinal != null
        ? `${info.contact_phone} (${info.phone_ordinal})`
        : id;
    return `${label} (${href})`;
  });
}

// Build a CallRefMap (call_id → phone + occurrence-index) from the
// ``call_refs`` array the AI endpoints return. Tolerant of the loose
// shapes coming off the wire so callers can pass raw API rows.
export function buildCallRefMap(
  refs:
    | ReadonlyArray<{
        call_id: string;
        contact_phone: string | null;
        phone_ordinal: number | null;
        canonical_call_id?: string | null;
      }>
    | null
    | undefined,
): CallRefMap {
  const map: CallRefMap = new Map();
  for (const r of refs ?? []) {
    if (!r?.call_id) continue;
    map.set(r.call_id, {
      contact_phone: r.contact_phone ?? null,
      phone_ordinal: r.phone_ordinal ?? null,
      canonical_call_id: r.canonical_call_id ?? null,
    });
  }
  return map;
}

// Rewrite raw Dialpad call IDs inside an HTML string into
// ``phone (ordinal)`` anchor links, walking only text nodes so we never
// corrupt existing markup or attribute values. Used for the Training
// Module HTML, which is rendered via dangerouslySetInnerHTML.
// ``absoluteHrefs`` makes the anchors point at absolute call-detail
// URLs — required for clipboard payloads that get pasted outside the
// app shell.
export function linkifyCallIdsInHtml(
  html: string,
  refMap?: CallRefMap,
  opts?: { absoluteHrefs?: boolean },
): string {
  if (typeof document === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let node: Node | null = walker.nextNode();
  while (node) {
    const text = node.nodeValue ?? "";
    // Skip text anywhere inside an anchor (including wrapped in nested
    // markup like <a><strong>id</strong></a>), and text with no call IDs.
    CALL_ID_RE.lastIndex = 0;
    if (!node.parentElement?.closest("a") && CALL_ID_RE.test(text)) {
      targets.push(node as Text);
    }
    node = walker.nextNode();
  }

  for (const textNode of targets) {
    const text = textNode.nodeValue ?? "";
    const frag = doc.createDocumentFragment();
    let last = 0;
    let m: RegExpExecArray | null;
    CALL_ID_RE.lastIndex = 0;
    while ((m = CALL_ID_RE.exec(text)) !== null) {
      if (m.index > last) {
        frag.appendChild(doc.createTextNode(text.slice(last, m.index)));
      }
      const id = m[0];
      const info = refMap?.get(id);
      // Alias refs (duplicate-leg IDs) link to the canonical call's page.
      const targetId = info?.canonical_call_id ?? id;
      const href = opts?.absoluteHrefs
        ? buildCallHref(targetId)
        : buildUrl("call-detail", null, null, { callId: targetId });
      const anchor = doc.createElement("a");
      anchor.setAttribute("href", href);
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
      anchor.setAttribute(
        "class",
        "text-primary underline underline-offset-2 hover:text-primary/80",
      );
      anchor.setAttribute("data-call-id", targetId);
      anchor.textContent =
        info && info.contact_phone && info.phone_ordinal != null
          ? `${info.contact_phone} (${info.phone_ordinal})`
          : id;
      frag.appendChild(anchor);
      last = m.index + id.length;
    }
    if (last < text.length) {
      frag.appendChild(doc.createTextNode(text.slice(last)));
    }
    textNode.parentNode?.replaceChild(frag, textNode);
  }

  return doc.body.innerHTML;
}
